import { ROUTES } from 'src/constants/routes.const';
import { getAudioComprehensionSessionUrl } from 'src/utils/getAudioComprehensionSessionUrl';
import { getSentenceBuildingSessionUrl } from 'src/utils/getSentenceBuildingSessionUrl';
import { getTextComprehensionSessionUrl } from 'src/utils/getTextComprehensionSessionUrl';
import { getVocabCheckSessionUrl } from 'src/utils/getVocabCheckSessionUrl';
import { getQuickReviewSessionUrl } from 'src/utils/getQuickReviewSessionUrl';
import { getPronunciationCheckSessionUrl } from 'src/utils/getPronunciationCheckSessionUrl';
import { getConversationCheckSessionUrl } from 'src/utils/getConversationCheckSessionUrl';
import { getPathAiConversationSessionUrl } from 'src/utils/getPathAiConversationSessionUrl';
import { ContentMedia } from 'src/api/media/types';
import { getPathImmersionSessionUrl } from 'src/utils/getPathImmersionSessionUrl';
import { getPathReviewSessionUrl } from 'src/utils/getPathReviewSessionUrl';
import { getPathSessionUrl } from 'src/utils/getPathSessionUrl';
import { CurrentPathStep } from './advancePathProgress';

export type PathStepLaunchResult =
  | { kind: 'url'; url: string }
  /** Unknown checkpoint test types with no launch UI - the caller must pass them and re-resolve. */
  | { kind: 'autoPassCheckpoint'; checkpointId: number };

export type BuildPathStepLaunchUrlOptions = {
  forReview?: boolean;
  /**
   * Previously picked immersion video (e.g. from the dashboard preview cache).
   * When set, immersion launch reuses it instead of picking again.
   */
  immersionMedia?: ContentMedia | null;
  /**
   * Upcoming wordlist items to skip when building a future checkpoint
   * session. Omitted from the URL when zero / unset.
   */
  futureLearnablesOffset?: number;
  /**
   * Preview of a checkpoint that is not the learner's current step.
   * Unknown types are not auto-passed. Finishing a real session still
   * includes `pathway_checkpoint_id` so progress teleports to the next leg,
   * unless the checkpoint is already completed (a retry).
   */
  tryNow?: boolean;
};

/**
 * Resolves the launch URL for a path step.
 *
 * Most steps resolve synchronously (a plain `PathStepLaunchResult`); immersion and
 * AI conversation need to look up a video/mission first, and review needs to
 * check whether anything is due, so they resolve as a `Promise`. Callers that care about showing a loading state while resolving can
 * check `result instanceof Promise` to tell the two apart without awaiting.
 */
export const buildPathStepLaunchUrl = (
  languagePairId: number,
  step: CurrentPathStep,
  {
    forReview = false,
    immersionMedia,
    futureLearnablesOffset,
    tryNow = false,
  }: BuildPathStepLaunchUrlOptions = {},
): PathStepLaunchResult | Promise<PathStepLaunchResult> => {
  if (step.kind === 'checkpoint') {
    const pathwayCheckpointId =
      step.checkpoint.status === 'completed' ? undefined : Number(step.checkpoint.id);
    const offsetOptions = futureLearnablesOffset !== undefined ? { futureLearnablesOffset } : {};

    switch (step.checkpoint.test) {
      case 'vocabularyCheck':
        return {
          kind: 'url',
          url: getVocabCheckSessionUrl(languagePairId, {
            source: 'path',
            back: ROUTES.path,
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'quickReview':
        return {
          kind: 'url',
          url: getQuickReviewSessionUrl(languagePairId, {
            source: 'path',
            back: ROUTES.path,
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'sentenceBuilding':
        return {
          kind: 'url',
          url: getSentenceBuildingSessionUrl(languagePairId, {
            source: 'path',
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'textComprehension':
        return {
          kind: 'url',
          url: getTextComprehensionSessionUrl(languagePairId, {
            source: 'path',
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'audioComprehension':
        return {
          kind: 'url',
          url: getAudioComprehensionSessionUrl(languagePairId, {
            source: 'path',
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'pronunciation':
        return {
          kind: 'url',
          url: getPronunciationCheckSessionUrl(languagePairId, {
            source: 'path',
            back: ROUTES.path,
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      case 'conversation':
        return {
          kind: 'url',
          url: getConversationCheckSessionUrl(languagePairId, {
            source: 'path',
            back: ROUTES.path,
            ...(pathwayCheckpointId !== undefined ? { pathwayCheckpointId } : {}),
            ...offsetOptions,
          }),
        };
      default:
        // Unknown checkpoint test types with no launch UI are passed automatically.
        // A Try now preview of an unknown type has no session and must not pass.
        if (tryNow || pathwayCheckpointId === undefined) {
          return { kind: 'url', url: ROUTES.path };
        }
        return { kind: 'autoPassCheckpoint', checkpointId: pathwayCheckpointId };
    }
  }

  const pathwayNodeId = Number(step.session.id);
  // Only include `forReview` when true, matching each URL builder's own convention
  // of omitting optional keys rather than passing an explicit falsy value.
  const reviewOptions = forReview ? { forReview } : {};

  if (step.session.sessionType === 'immersion') {
    return getPathImmersionSessionUrl(languagePairId, {
      pathwayNodeId,
      ...reviewOptions,
      ...(immersionMedia !== undefined ? { media: immersionMedia } : {}),
    }).then(mediaUrl => ({
      kind: 'url' as const,
      url:
        mediaUrl ??
        getPathSessionUrl(languagePairId, 'immersion', { pathwayNodeId, ...reviewOptions }),
    }));
  }

  if (step.session.sessionType === 'aiConversation') {
    return getPathAiConversationSessionUrl(languagePairId, {
      pathwayNodeId,
      ...reviewOptions,
    }).then(membotUrl => ({
      kind: 'url' as const,
      url:
        membotUrl ??
        getPathSessionUrl(languagePairId, 'aiConversation', { pathwayNodeId, ...reviewOptions }),
    }));
  }

  if (step.session.sessionType === 'review') {
    return getPathReviewSessionUrl(languagePairId, { pathwayNodeId, ...reviewOptions }).then(
      url => ({ kind: 'url' as const, url }),
    );
  }

  return {
    kind: 'url',
    url: getPathSessionUrl(languagePairId, step.session.sessionType, {
      pathwayNodeId,
      ...reviewOptions,
    }),
  };
};
